import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import {
  formatDecisionImprovementDisplay,
  formatNumber,
  formatValueWithUnit,
  humanizeColumnHeader,
  humanizeSnakeCase,
} from "../utils/decisionFormatters";

/** Prefer `baseline`; if present, legacy `before_filter` is omitted from columns. */
function pruneLegacyBeforeKey(keysArr) {
  const hasBaseline = keysArr.includes("baseline");
  return keysArr.filter(
    (k) =>
      k !== "change" &&
      k !== "unit" &&
      k !== "improvement_sign" &&
      k !== "improvement_value" &&
      !(hasBaseline && k === "before_filter")
  );
}

/**
 * Name first, then baseline/before_filter → after_filter, then other keys A–Z, then `improvement` if present.
 * @param {string[]} base
 */
function sortBaseColumns(base) {
  const special = new Set(["name", "baseline", "before_filter", "after_filter"]);
  const others = base.filter((k) => !special.has(k) && k !== "improvement");
  others.sort((a, b) => a.localeCompare(b));
  const out = [];
  if (base.includes("name")) out.push("name");
  if (base.includes("baseline")) out.push("baseline");
  else if (base.includes("before_filter")) out.push("before_filter");
  if (base.includes("after_filter")) out.push("after_filter");
  for (const k of others) out.push(k);
  if (base.includes("improvement")) out.push("improvement");
  return out;
}

/** @param {Record<string, unknown>} row */
function rowHasImprovementFields(row) {
  if (row == null || typeof row !== "object") return false;
  if (typeof row.improvement === "string" && row.improvement.trim()) return true;
  if (row.improvement_sign != null && typeof row.improvement_value === "number" && Number.isFinite(row.improvement_value)) {
    return true;
  }
  return false;
}

/**
 * Infer columns; optional `improvement` column from data (string or sign+value), always last when present.
 * @param {Record<string, unknown>[]} rows
 */
function inferColumns(rows) {
  const keys = new Set();
  for (const row of rows) {
    if (row && typeof row === "object" && !Array.isArray(row)) {
      Object.keys(row).forEach((k) => keys.add(k));
    }
  }
  const base = pruneLegacyBeforeKey(Array.from(keys));
  const ordered = sortBaseColumns(base);
  const anyImprovement = rows.some(rowHasImprovementFields);
  if (anyImprovement && !ordered.includes("improvement")) {
    ordered.push("improvement");
  }
  return ordered;
}

function formatCell(columnKey, row) {
  const unit = row.unit;
  if (columnKey === "improvement") {
    return formatDecisionImprovementDisplay(row);
  }
  if (columnKey === "name" && typeof row.name === "string") {
    return humanizeSnakeCase(row.name);
  }
  const v = row[columnKey];
  if (v == null) return "—";
  if (typeof v === "number") {
    if (columnKey === "baseline" || columnKey === "before_filter" || columnKey === "after_filter") {
      return formatValueWithUnit(v, typeof unit === "string" ? unit : undefined);
    }
    if (columnKey === "train" || columnKey === "validation") {
      return formatNumber(v);
    }
    return formatNumber(v);
  }
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

/**
 * @param {{ rows: Record<string, unknown>[]; dense?: boolean; nameColumnHeader?: string; containerSx?: object; 'aria-label'?: string }} props
 */
export default function GenericObjectTable({
  rows,
  dense = true,
  nameColumnHeader,
  containerSx,
  "aria-label": ariaLabel = "Data table",
}) {
  if (!Array.isArray(rows) || rows.length === 0) return null;
  const clean = rows.filter((r) => r && typeof r === "object" && !Array.isArray(r));
  if (clean.length === 0) return null;
  const columns = inferColumns(clean);

  function headerForColumn(col) {
    if (col === "name" && typeof nameColumnHeader === "string" && nameColumnHeader.trim()) {
      return nameColumnHeader.trim();
    }
    return humanizeColumnHeader(col);
  }

  return (
    <TableContainer
      component={Paper}
      variant="outlined"
      sx={{
        borderRadius: 2,
        my: 1,
        width: "100%",
        maxWidth: { xs: "100%", sm: 640, md: 720 },
        alignSelf: "flex-start",
        ...containerSx,
      }}
    >
      <Table size={dense ? "small" : "medium"} aria-label={ariaLabel}>
        <TableHead>
          <TableRow>
            {columns.map((col) => (
              <TableCell key={col} sx={{ fontWeight: 600, bgcolor: "action.hover" }}>
                {headerForColumn(col)}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {clean.map((row, idx) => (
            <TableRow key={idx}>
              {columns.map((col) => (
                <TableCell key={col}>{formatCell(col, row)}</TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
